import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { format, getMonth, getYear } from 'date-fns';
import { range } from 'es-toolkit/compat';
import ReactDatePicker, { CalendarContainer, CalendarContainerProps, ReactDatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ArrowLeft, ArrowRight, Calendar, Cross } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import './styles.css';

export interface CustomDatePickerInputProps {
  value?: string;
  onClick?: () => void;
  showTimeSelectOnly?: boolean;
  className?: string;
  isOpen: boolean;
  customInputProps?: any;
  disabled?: boolean;
}

type Props = {
  selectedDate?: Date;
  onChange?: (date: Date) => void;
  CustomInput?: React.ElementType<CustomDatePickerInputProps>;
  customInputProps?: any;
  disabled?: boolean;
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const monthOptions = months.map((month, index) => ({ value: index, label: month }));

// @ts-ignore
const DefaultCustomInput = forwardRef(
  (
    {
      value,
      onClick,
      showTimeSelectOnly,
      className,
      isOpen,
      customInputProps,
      disabled,
      ...rest
    }: CustomDatePickerInputProps,
    ref
  ) => {
    const selectedDate = value ? new Date(value) : 'None';
    const formattedDate = showTimeSelectOnly
      ? value
      : selectedDate !== 'None'
        ? format(selectedDate, 'MMM dd, yyyy')
        : selectedDate;

    const theme = useTheme();

    return (
      <Button
        StartIcon={Calendar}
        variant="tertiary"
        style={{
          paddingLeft: 0,
          borderBottom: isOpen ? `1px solid ${theme.colors.button.blue.focus}` : `1px dashed ${theme.colors.text.g3}`,
          borderRadius: 0,
          display: 'flex',
          justifyContent: 'flex-start',
          minWidth: '120px',
        }}
        onClick={onClick}
        ref={ref}
        sx={{
          _hover: {
            borderBottom: `1px solid ${theme.colors.text.g1} !important`,
          },
          _focus: {
            borderBottom: `1px solid ${theme.colors.button.blue.focus} !important`,
          },
          p: {
            color: '#333333 !important',
            fontWeight: 400,
            m: '0',
          },
          svg: {
            color: isOpen ? `${theme.colors.button.blue.focus} !important` : `${theme.colors.text.g2} !important`,
          },
        }}
        className={`mesh-date-picker-button ${className}`}
        disabled={disabled}
        {...customInputProps}
      >
        {formattedDate}
      </Button>
    );
  }
);

const CustomCalendarContainer = ({
  className,
  children,
  onClose,
  showTimeSelectOnly,
  customInputProps = {},
}: CalendarContainerProps & {
  onClose: () => void;
  showTimeSelectOnly?: boolean;
  customInputProps?: any;
}) => {
  if (showTimeSelectOnly) {
    return (
      <Box
        sx={{
          padding: '8px',
          borderRadius: '8px',
          background: '#FFFFFF',
          boxShadow: '2px 0px 4px 3px rgba(0, 0, 0, 0.12), 0px 4px 4px 3px rgba(0, 0, 0, 0.08)',
        }}
      >
        <CalendarContainer className={className}>{children}</CalendarContainer>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        padding: '12px',
        borderRadius: '8px',
        height: 'auto',
        display: 'flex',
        background: '#FFFFFF',
        flexDirection: 'column',
        boxShadow: '2px 0px 4px 3px rgba(0, 0, 0, 0.12), 0px 4px 4px 3px rgba(0, 0, 0, 0.08)',
        // resetting globalnav.css styles
        '& .react-datepicker__month-container': {
          width: 'auto',
          fontSize: 'medium',
        },
      }}
    >
      <Flex p="12px" mb="12px" alignItems="center">
        <TextBodyMedium bold>Select a Date</TextBodyMedium>
        <Cross ml="auto" onClick={onClose} cursor="pointer" />
      </Flex>
      <CalendarContainer className={className}>{children}</CalendarContainer>

      {customInputProps?.isClearableFromCalendar && (
        <Box pt={4}>
          <Button
            variant="tertiary"
            onClick={() => {
              customInputProps?.handleOnClear();
              onClose();
            }}
          >
            Clear
          </Button>
        </Box>
      )}
    </Box>
  );
};

const DatePicker = ({
  CustomInput = DefaultCustomInput,
  onChange = () => {},
  selectedDate,
  showTimeSelect,
  showTimeSelectOnly,
  timeIntervals,
  timeCaption,
  dateFormat,
  // make sure minDate and maxDate are mostly same across rerenders,
  // as they are used to regenerate year options, which results in a weird issue in menu list where it does not scroll to selected option
  // so, make sure minDate maxDate have fixed time and are not created with new Date(), which will have changed seconds in every rerender
  minDate = null,
  maxDate = null,
  customInputProps = {},
  ...props
}: ReactDatePickerProps & Props) => {
  const datePickerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const startYear = minDate ? getYear(minDate) : 1990;
    const endYear = maxDate ? getYear(maxDate) : getYear(new Date()) + 2;
    const years = range(startYear, endYear + 1, 1);
    const yearOptions = years.map((year) => ({ value: year, label: year }));
    return yearOptions;
  }, [maxDate?.toString(), minDate?.toString()]);

  const onInputClick = () => {
    setIsOpen(true);
  };

  const [startDate, setStartDate] = useState(selectedDate);

  useEffect(() => {
    setStartDate(selectedDate);
  }, [selectedDate]);

  const onDateChange = (date: Date) => {
    setStartDate(date);
    onChange && onChange(date);
    setIsOpen(false);
  };
  return (
    <ReactDatePicker
      popperClassName="calendar-popper"
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <Flex alignItems="center" justifyContent="space-between">
          <ArrowButton
            onClick={decreaseMonth}
            StartIcon={ArrowLeft}
            variant="tertiary"
            disabled={prevMonthButtonDisabled}
          />
          <Box>
            {/* @ts-ignore */}
            <SelectDashed
              value={monthOptions.filter(({ value }) => value === getMonth(date))[0]}
              handleChange={({ value }) => changeMonth(value)}
              options={monthOptions}
              isSearchable={false}
              minWidth="min-content"
              boldText
            />
          </Box>

          <Box>
            {/* @ts-ignore */}
            <SelectDashed
              value={yearOptions.filter(({ value }) => value === getYear(date))[0]}
              handleChange={({ value }) => changeYear(Number(value))}
              options={yearOptions}
              isSearchable={false}
              minWidth="min-content"
              boldText
            />
          </Box>

          <ArrowButton
            variant="tertiary"
            StartIcon={ArrowRight}
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
          />
        </Flex>
      )}
      selected={startDate}
      showPopperArrow={false}
      calendarClassName="mesh-datepicker"
      calendarContainer={(props) => (
        <CustomCalendarContainer
          {...props}
          onClose={() => {
            datePickerRef.current?.setOpen(false);
          }}
          showTimeSelectOnly={showTimeSelectOnly}
          customInputProps={customInputProps}
        />
      )}
      onChange={onDateChange}
      customInput={
        <CustomInput
          showTimeSelectOnly={showTimeSelectOnly}
          className={isOpen ? 'mesh-date-picker-button-open' : 'mesh-date-picker-button'}
          isOpen={isOpen}
          customInputProps={customInputProps}
          disabled={props.disabled}
        />
      }
      showTimeSelect={showTimeSelect}
      showTimeSelectOnly={showTimeSelectOnly}
      timeIntervals={timeIntervals}
      timeCaption={timeCaption}
      ref={datePickerRef}
      dateFormat={dateFormat}
      minDate={minDate}
      maxDate={maxDate}
      onInputClick={onInputClick}
      onCalendarClose={() => setIsOpen(false)}
      popperModifiers={
        isMobileView()
          ? [
              {
                name: 'preventOverflow',
                options: {
                  rootBoundary: 'viewport',
                  tether: false,
                  altAxis: true,
                },
              },
            ]
          : []
      }
      {...props}
    />
  );
};

const ArrowButton = styled(Button)`
  margin-left: 6px;
  svg {
    color: #333333 !important;
  }
  visibility: ${(props) => (props.disabled ? 'hidden' : 'visible')};
`;

export { DatePicker };
